/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { authClientsDetachFromMcpServer } from "../funcs/authClientsDetachFromMcpServer.js";
import { authClientsGet } from "../funcs/authClientsGet.js";
import { authClientsGetMcpServerAuthentication } from "../funcs/authClientsGetMcpServerAuthentication.js";
import { authClientsList } from "../funcs/authClientsList.js";
import { authClientsSetMcpServerUpstreamAuthRequirement } from "../funcs/authClientsSetMcpServerUpstreamAuthRequirement.js";
import { authClientsUpdate } from "../funcs/authClientsUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AuthClient } from "../models/components/authclient.js";
import { McpServerAuthentication } from "../models/components/mcpserverauthentication.js";
import {
  DetachAuthClientFromMcpServerRequest,
  DetachAuthClientFromMcpServerSecurity,
} from "../models/operations/detachauthclientfrommcpserver.js";
import {
  GetAuthClientRequest,
  GetAuthClientSecurity,
} from "../models/operations/getauthclient.js";
import {
  GetMcpServerAuthenticationRequest,
  GetMcpServerAuthenticationSecurity,
} from "../models/operations/getmcpserverauthentication.js";
import {
  ListAuthClientsRequest,
  ListAuthClientsResponse,
  ListAuthClientsSecurity,
} from "../models/operations/listauthclients.js";
import {
  SetMcpServerUpstreamAuthRequirementRequest,
  SetMcpServerUpstreamAuthRequirementSecurity,
} from "../models/operations/setmcpserverupstreamauthrequirement.js";
import {
  UpdateAuthClientRequest,
  UpdateAuthClientSecurity,
} from "../models/operations/updateauthclient.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class AuthClients extends ClientSDK {
  /**
   * detachAuthClientFromMcpServer authClients
   *
   * @remarks
   * Stop an MCP server offering an auth client in its typed auth configuration. The client and its other servers are unchanged. Takes effect only where typed auth client resolution is enforced. It revokes no upstream credentials and does not remove the server's existing upstream OAuth association, which keeps serving elsewhere, including in shadow mode. Requires org:admin.
   */
  async detachFromMcpServer(
    request: DetachAuthClientFromMcpServerRequest,
    security?: DetachAuthClientFromMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsDetachFromMcpServer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getAuthClient authClients
   *
   * @remarks
   * Get an auth client in the caller's organization. Requires org:read.
   */
  async get(
    request: GetAuthClientRequest,
    security?: GetAuthClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMcpServerAuthentication authClients
   *
   * @remarks
   * Get how an MCP server authenticates to its upstream: whether authentication is required and which auth clients it offers. Requires org:read.
   */
  async getMcpServerAuthentication(
    request: GetMcpServerAuthenticationRequest,
    security?: GetMcpServerAuthenticationSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServerAuthentication> {
    return unwrapAsync(authClientsGetMcpServerAuthentication(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listAuthClients authClients
   *
   * @remarks
   * List the auth clients in the caller's organization. Requires org:read.
   */
  async list(
    request?: ListAuthClientsRequest | undefined,
    security?: ListAuthClientsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListAuthClientsResponse, { cursor: string }>> {
    return unwrapResultIterator(authClientsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setMcpServerUpstreamAuthRequirement authClients
   *
   * @remarks
   * Declare whether an MCP server's upstream needs authentication. Where typed auth client resolution is enforced, a server that needs none is served without upstream credentials and a server that needs it and offers no enabled client is unavailable until setup is complete; elsewhere, including in shadow mode, the server's existing upstream configuration keeps serving. Not available for hosted toolsets. Requires org:admin.
   */
  async setMcpServerUpstreamAuthRequirement(
    request: SetMcpServerUpstreamAuthRequirementRequest,
    security?: SetMcpServerUpstreamAuthRequirementSecurity | undefined,
    options?: RequestOptions,
  ): Promise<McpServerAuthentication> {
    return unwrapAsync(authClientsSetMcpServerUpstreamAuthRequirement(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateAuthClient authClients
   *
   * @remarks
   * Rename, enable or disable an auth client. The change applies to the typed auth configuration of every MCP server that offers the client and takes effect only where typed auth client resolution is enforced. It revokes no upstream credentials; elsewhere, including in shadow mode, an MCP server's existing upstream configuration keeps serving. Requires org:admin.
   */
  async update(
    request: UpdateAuthClientRequest,
    security?: UpdateAuthClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AuthClient> {
    return unwrapAsync(authClientsUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
